import { useEffect } from 'react'
import Dinero from 'dinero.js'
import { useFormikContext } from 'formik'
import styled from 'styled-components'
import { Button, Grid, H } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'

import CircularRadioGroup from 'components/form/circular-radio-group'
import FakeInput from 'components/fake-input'
import IconList from 'components/icon-list'
import ExternalLink from 'components/external-link'
import PaymentFormFields from 'components/payment-form-fields'
import ENV from 'config/environment'
import {
  FuneralLeadAttributes,
  FuneralPlanLeadAttributes,
  FuneralQuoteAttributes,
  LeadAttributes,
  LpaLeadAttributes,
  WillLeadAttributes,
} from 'lib/models/lead'
import { PRODUCTS } from 'utils/enums'

import { PAYMENT_METHODS } from './helpers'
import PaymentIdentifierForm from './payment-identifier-form'

Dinero.globalLocale = 'en-GB'
Dinero.defaultCurrency = 'GBP'

type TakePaymentProps = {
  calculatedPrice: number
  skip?: boolean
  editablePaymentReference?: boolean
}
const TakePayment = ({
  calculatedPrice,
  skip,
  editablePaymentReference = false,
}: TakePaymentProps) => {
  const { values, handleSubmit, setFieldValue } = useFormikContext()

  const { quoteAttributes, product } = values as
    | FuneralLeadAttributes
    | WillLeadAttributes
    | LpaLeadAttributes
    | FuneralPlanLeadAttributes

  const allowsMultiplePaymentMethods = product === 'funeral'

  const paymentMethod =
    quoteAttributes.paymentMethod ??
    (allowsMultiplePaymentMethods
      ? PAYMENT_METHODS.compass
      : PAYMENT_METHODS.card)

  useEffect(() => {
    if (product === PRODUCTS.FUNERAL_PLAN) {
      setFieldValue('quoteAttributes.paymentAmount', calculatedPrice)
    }
  }, [calculatedPrice, product, setFieldValue])

  return (
    <Grid>
      <Grid.Item span={10}>
        <Grid.Item>
          <H size="S">Take payment</H>
        </Grid.Item>
        {skip ? (
          <Grid.Item>↑ Complete sections above first</Grid.Item>
        ) : (
          <>
            <Grid margin={['M', 0]}>
              <Grid.Item span={6}>
                <FakeInput
                  label="Amount (£)"
                  className="quoteAttributes.paymentAmount"
                  value={Dinero({
                    amount: calculatedPrice || 0,
                  }).toFormat('$0,0')}
                />
              </Grid.Item>

              {allowsMultiplePaymentMethods && (
                <Grid.Item>
                  <PaymentMethodOptions
                    calculatedPrice={calculatedPrice}
                    handleSave={() => handleSubmit()}
                  />
                </Grid.Item>
              )}
            </Grid>
            {paymentMethod === PAYMENT_METHODS.compass && (
              <>
                <Grid>
                  <PaymentFormFields
                    namespace="quoteAttributes.compassPayment"
                    type="compass"
                    handleSave={() => handleSubmit()}
                  />
                </Grid>
              </>
            )}

            {paymentMethod === PAYMENT_METHODS.card && (
              <Grid margin={['XL', 0]}>
                <SquarePayment
                  editablePaymentReference={editablePaymentReference}
                />
              </Grid>
            )}
          </>
        )}
      </Grid.Item>
    </Grid>
  )
}
export default TakePayment

const StyledPaymentMethodGridItem = styled(Grid.Item)`
  width: max-content;
`

const getOptions = (values: LeadAttributes) => {
  return [
    // Make Compass prioritised for funerals
    ...(values.product === PRODUCTS.FUNERAL
      ? [
          {
            label: 'Compass',
            value: PAYMENT_METHODS.compass,
          },
        ]
      : []),
    {
      label: 'Card',
      value: PAYMENT_METHODS.card,
    },
  ]
}
const PaymentMethodOptions = ({
  calculatedPrice,
  handleSave,
}: {
  calculatedPrice: number
  handleSave: (data: { name: string; value: string }) => void
}) => {
  const { values, setFieldValue } = useFormikContext<LeadAttributes>()
  return (
    <StyledPaymentMethodGridItem margin={['M', 0, 'L', 0]}>
      <CircularRadioGroup
        label="Method of payment"
        name="quoteAttributes.paymentMethod"
        inline
        options={getOptions(values)}
        handleSave={(data) => {
          if (data.value === 'invoice') {
            if (
              (values.quoteAttributes as FuneralQuoteAttributes).invoice
                ?.amount == null
            ) {
              setFieldValue(
                'quoteAttributes.invoice.amount',
                calculatedPrice / 100
              )
            }
          } else if (data.value === 'card') {
            setFieldValue(
              'quoteAttributes.paymentAmount',
              calculatedPrice / 100
            )
          } else if (data.value === 'compass') {
            setFieldValue(
              'quoteAttributes.compassPayment.amount',
              calculatedPrice / 100
            )
          }
          handleSave(data)
        }}
      />
    </StyledPaymentMethodGridItem>
  )
}

const SquarePayment = ({
  editablePaymentReference,
}: {
  editablePaymentReference: boolean
}) => {
  return (
    <>
      <Grid.Item>
        <IconList
          items={[
            'Pause call recording before taking payment details',
            <>
              Once the payment has been made,{' '}
              <ExternalLink
                href={`${ENV.SQUARE_DASHBOARD_URL}/sales/transactions`}
              >
                find the transaction in Square
              </ExternalLink>
              , click in to it, and then copy the URL in to the field below.
            </>,
          ]}
        />

        <Button.Primary
          style={{ paddingTop: GTR.XXS, paddingBottom: GTR.XXS }}
          type="button"
          onClick={() => {
            window.open(ENV.SQUARE_DASHBOARD_URL, '_blank')
          }}
        >
          Take payment
        </Button.Primary>
      </Grid.Item>
      <Grid.Item>
        <PaymentIdentifierForm
          editablePaymentReference={editablePaymentReference}
        />
      </Grid.Item>
    </>
  )
}
